// Bandeau promo : la campagne en cours, sinon le prix de lancement. Données :
// ReactShell::PromoBanner (émoji, texte, code et date déjà calculés).
//
// Fermé, il reste masqué pour la session, pour ce code-là (sessionStorage
// promo_banner_dismissed) : une nouvelle campagne se montre à nouveau.
//
// Rendu masqué côté serveur ; le <script> qui suit l'affiche avant le premier
// affichage. Après l'hydratation (ou lors d'un montage
// côté client, où ce script ne s'exécute pas), l'état React prend le relais.
import { useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import AppLink from '@/components/ui/AppLink'

export type PromoBannerData = {
  icon: string
  message: string
  code: string
  suffix: string
  cta: string
  copied: string
  pricingPath: string
}

const DISMISSED_KEY = 'promo_banner_dismissed'
// Vrai dans le HTML du serveur et pendant l'hydratation, faux ensuite : un
// bandeau monté côté client (après une navigation) n'a pas besoin du script,
// que React n'exécuterait pas (et signale en développement).
const noopSubscribe = () => () => {}
const useServerMarkup = () => useSyncExternalStore(noopSubscribe, () => false, () => true)

// Le code est une chaîne JSON dans le script : majuscules, chiffres et _ côté
// serveur, mais un « < » n'y fermerait jamais la balise.
const revealScript = (code: string) => `
  if (sessionStorage.getItem('${DISMISSED_KEY}') !== ${JSON.stringify(code).replace(/</g, '\\u003c')}) {
    document.getElementById('launch-promo-banner').style.display = '';
  }
`

export default function PromoBanner({ data }: { data: Record<string, any> }) {
  const promo = data as PromoBannerData
  const [visible, setVisible] = useState(false)
  const [copied, setCopied] = useState(false)
  const serverMarkup = useServerMarkup()
  const copyTimer = useRef<number | undefined>(undefined)

  useLayoutEffect(() => {
    setVisible(sessionStorage.getItem(DISMISSED_KEY) !== promo.code)
  }, [promo.code])
  useLayoutEffect(() => () => window.clearTimeout(copyTimer.current), [])

  const copyCode = () => {
    navigator.clipboard?.writeText(promo.code)?.catch(() => {})
    setCopied(true)
    window.clearTimeout(copyTimer.current)
    copyTimer.current = window.setTimeout(() => setCopied(false), 1500)
  }

  const dismiss = () => {
    setVisible(false)
    sessionStorage.setItem(DISMISSED_KEY, promo.code)
  }

  return (
    <>
      {/* L'attribut style diffère légitimement après le script : pas d'alerte d'hydratation. */}
      <div id="launch-promo-banner" className="launch-promo-banner" style={visible ? undefined : { display: 'none' }} suppressHydrationWarning>
        <div className="launch-promo-content">
          <span className="launch-promo-icon">{promo.icon}</span>{' '}
          <span className="launch-promo-text">
            {` ${promo.message} `}
            <span className="launch-promo-code" onClick={copyCode}>
              {copied ? promo.copied : promo.code}
            </span>
            {` ${promo.suffix} `}
          </span>{' '}
          <AppLink className="launch-promo-cta" href={promo.pricingPath}>
            {` ${promo.cta} → `}
          </AppLink>
        </div>{' '}
        <button className="launch-promo-close" onClick={dismiss} aria-label="Close">
          &times;
        </button>
      </div>

      {serverMarkup && <script dangerouslySetInnerHTML={{ __html: revealScript(promo.code) }} />}
    </>
  )
}
